/**
 * Camera direction display contract (C-10).
 *
 * Source of truth: Camera API/model field ``direction``.
 * Allowed values: ENTRY | EXIT | BIDIRECTIONAL (case-insensitive).
 * Missing, blank, or unrecognized values never invent a direction.
 */

export type CameraDirectionCode = 'ENTRY' | 'EXIT' | 'BIDIRECTIONAL';

export type CameraDirectionIcon =
    | 'arrow-right-to-bracket'
    | 'arrow-right-from-bracket'
    | 'arrow-right-arrow-left';

export type CameraDirectionDisplay = {
    code: CameraDirectionCode;
    label: string;
    icon: CameraDirectionIcon;
};

const DIRECTION_DISPLAY: Record<CameraDirectionCode, CameraDirectionDisplay> = {
    ENTRY: {
        code: 'ENTRY',
        label: 'Entry',
        icon: 'arrow-right-to-bracket',
    },
    EXIT: {
        code: 'EXIT',
        label: 'Exit',
        icon: 'arrow-right-from-bracket',
    },
    BIDIRECTIONAL: {
        code: 'BIDIRECTIONAL',
        label: 'Bidirectional',
        icon: 'arrow-right-arrow-left',
    },
};

/** Normalize API direction to a known code, or null when missing/unknown. */
export function normalizeCameraDirection(
    value: string | null | undefined,
): CameraDirectionCode | null {
    const normalized = String(value ?? '')
        .trim()
        .toUpperCase();
    if (normalized === 'ENTRY' || normalized === 'EXIT' || normalized === 'BIDIRECTIONAL') {
        return normalized;
    }
    return null;
}

/** Full display model for known directions; null when direction is absent/unknown. */
export function resolveCameraDirectionDisplay(
    value: string | null | undefined,
): CameraDirectionDisplay | null {
    const code = normalizeCameraDirection(value);
    return code ? DIRECTION_DISPLAY[code] : null;
}

/** Human-readable label, or em dash when direction cannot be resolved. */
export function formatCameraDirectionLabel(value: string | null | undefined): string {
    return resolveCameraDirectionDisplay(value)?.label ?? '—';
}
